import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { Input } from '../../ui/Input';
import { Spinner } from '../../ui/Spinner';
import { FollowUpForm } from './FollowUpForm';
import { useAuth } from '../../../contexts/AuthContext';
import { contactService } from '../../../services/contactService';
import { followUpService } from '../../../services/followUpService';

// person: { contactId, connectionId, name } preselects the person.
// people: optional list of contacts to choose from (e.g. the people in a Moment).
// reminder: edit mode (date and note only).
export const FollowUpModal = ({ person, people, momentId, momentTitle, reminder, onClose, onSaved }) => {
  const { user } = useAuth();
  const [choices, setChoices] = useState(people || []);
  const [loading, setLoading] = useState(!person && !reminder && !people);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(
    person || (people?.length === 1 ? { contactId: people[0].id, name: people[0].full_name } : null)
  );

  useEffect(() => {
    if (person || reminder || people) return;
    contactService.getContacts()
      .then(setChoices)
      .catch((e) => setLoadError(e.message || 'Could not load contacts.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async ({ day, note }) => {
    if (reminder) {
      const row = await followUpService.update(reminder.id, user.id, { day, note });
      onSaved(row, 'Follow-up updated.');
      return;
    }
    if (!picked) throw new Error('Choose who this follow-up is for.');
    const row = await followUpService.create({
      userId: user.id,
      contactId: picked.contactId,
      connectionId: picked.connectionId,
      day,
      note,
      momentId
    });
    onSaved(row, 'Follow-up added.');
  };

  const s = search.trim().toLowerCase();
  const shown = choices.filter((c) => !s || (c.full_name || '').toLowerCase().includes(s) || (c.company || '').toLowerCase().includes(s));

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4 max-h-[92vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{reminder ? 'Edit follow-up' : 'New follow-up'}</h3>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100" aria-label="Close">
            <SafeIcon icon={FiIcons.FiX} />
          </button>
        </div>
        {momentTitle && <p className="text-xs text-slate-500">Linked to Moment: {momentTitle}</p>}
        {!reminder && (
          picked ? (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
              <span className="text-sm font-semibold text-slate-800">{picked.name || 'Contact'}</span>
              {!person && (people?.length !== 1) && (
                <button type="button" onClick={() => setPicked(null)} className="text-xs font-bold text-slate-500 hover:text-slate-900">Change</button>
              )}
            </div>
          ) : loading ? (
            <div className="flex justify-center py-4"><Spinner size="sm" /></div>
          ) : (
            <div className="space-y-2">
              <Input placeholder="Search people..." value={search} onChange={(e) => setSearch(e.target.value)} />
              {loadError && <p className="text-sm text-red-600">{loadError}</p>}
              <div className="max-h-48 overflow-y-auto space-y-1">
                {shown.length === 0 && <p className="text-sm text-slate-500 py-2">No matching people.</p>}
                {shown.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setPicked({ contactId: c.id, name: c.full_name || 'Unnamed contact' })}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100"
                  >
                    <span className="text-sm font-semibold text-slate-800">{c.full_name || 'Unnamed contact'}</span>
                    {c.company && <span className="text-xs text-slate-500"> · {c.company}</span>}
                  </button>
                ))}
              </div>
            </div>
          )
        )}
        {(picked || reminder) && (
          <FollowUpForm
            initial={reminder}
            submitLabel={reminder ? 'Save changes' : 'Create follow-up'}
            onSubmit={handleSubmit}
            onCancel={onClose}
          />
        )}
      </div>
    </div>
  );
};